import type { ImagingAssetKind } from "elephant-web/gql";

const OFFLINE_CACHE_NAME = "epocus-imaging-offline-v1";
const SESSION_BLOB_LIMIT = 12;

const sessionBlobs = new Map<string, Blob>();

const storeKey = (imagingInstanceId: string, kind: ImagingAssetKind) =>
  `${kind}:${imagingInstanceId}`;

const cacheRequestUrl = (imagingInstanceId: string, kind: ImagingAssetKind) =>
  `${window.location.origin}/__epocus-imaging/${kind}/${encodeURIComponent(imagingInstanceId)}`;

const openOfflineCache = async (): Promise<Cache | null> => {
  if (typeof caches === "undefined") return null;
  try {
    return await caches.open(OFFLINE_CACHE_NAME);
  } catch {
    return null;
  }
};

export const readSessionBlob = (
  imagingInstanceId: string,
  kind: ImagingAssetKind,
): Blob | null => sessionBlobs.get(storeKey(imagingInstanceId, kind)) ?? null;

export const writeSessionBlob = (
  imagingInstanceId: string,
  kind: ImagingAssetKind,
  blob: Blob,
): void => {
  if (sessionBlobs.size >= SESSION_BLOB_LIMIT) {
    const oldest = sessionBlobs.keys().next().value;
    if (oldest) sessionBlobs.delete(oldest);
  }
  sessionBlobs.set(storeKey(imagingInstanceId, kind), blob);
};

export const readOfflineBlob = async (
  imagingInstanceId: string,
  kind: ImagingAssetKind,
): Promise<Blob | null> => {
  const cache = await openOfflineCache();
  if (!cache) return null;
  try {
    const hit = await cache.match(cacheRequestUrl(imagingInstanceId, kind));
    return hit ? await hit.blob() : null;
  } catch {
    return null;
  }
};

export const writeOfflineBlob = async (
  imagingInstanceId: string,
  kind: ImagingAssetKind,
  blob: Blob,
): Promise<boolean> => {
  const cache = await openOfflineCache();
  if (!cache) return false;
  try {
    await cache.put(
      cacheRequestUrl(imagingInstanceId, kind),
      new Response(blob, { headers: { "content-type": blob.type } }),
    );
    return true;
  } catch {
    return false;
  }
};

export const hasOfflineBlob = async (
  imagingInstanceId: string,
  kind: ImagingAssetKind,
): Promise<boolean> => {
  const cache = await openOfflineCache();
  if (!cache) return false;
  try {
    return !!(await cache.match(cacheRequestUrl(imagingInstanceId, kind)));
  } catch {
    return false;
  }
};

export const readCachedBlob = async (
  imagingInstanceId: string,
  kind: ImagingAssetKind,
): Promise<Blob | null> =>
  readSessionBlob(imagingInstanceId, kind) ??
  (await readOfflineBlob(imagingInstanceId, kind));

export const fetchBlobWithProgress = async (
  url: string,
  {
    expectedBytes,
    signal,
    onProgress,
  }: {
    expectedBytes?: number | null;
    signal?: AbortSignal;
    onProgress?: (fraction: number | null) => void;
  } = {},
): Promise<Blob> => {
  // The preview is already in the http cache as a no-CORS <img> response, which
  // a CORS fetch would reuse and then reject. Reloading forces a response that
  // actually carries the origin grant.
  const response = await fetch(url, { signal, cache: "reload" });
  if (!response.ok) {
    throw new Error(
      `Imaging asset fetch failed with status ${response.status}`,
    );
  }

  const declared = Number(response.headers.get("content-length"));
  const total =
    Number.isFinite(declared) && declared > 0 ? declared : (expectedBytes ?? 0);

  if (!response.body) {
    onProgress?.(null);
    return response.blob();
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    chunks.push(value);
    received += value.length;
    onProgress?.(total > 0 ? Math.min(1, received / total) : null);
  }

  return new Blob(chunks, {
    type: response.headers.get("content-type") ?? "video/mp4",
  });
};
